/**
 * SurveyProgressBar
 *
 * A sticky progress bar that displays survey completion status.
 * Shows percentage complete, current section, and estimated time remaining.
 * Stays visible at the top of the viewport while scrolling.
 *
 * @module components/survey/SurveyProgressBar
 */

import { useState, useEffect } from 'react';

// MUI Components
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

// Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

// MUI Utilities
import { alpha } from '@mui/material/styles';

// Utilities
import { formatTimeRemaining } from '@/utils/surveyHelpers';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Progress calculation result from calculateSurveyProgress()
 */
export interface SurveyProgress {
  /** Number of questions with valid answers */
  answered: number;
  /** Total number of visible questions */
  total: number;
  /** Completion percentage (0-100) */
  percentage: number;
  /** Estimated minutes remaining */
  estimatedMinutesRemaining: number;
}

/**
 * Props for SurveyProgressBar component
 */
export interface SurveyProgressBarProps {
  /** Progress calculation data */
  progress: SurveyProgress;
  /** Name of current section (optional) */
  currentSection?: string;
  /** Total number of sections */
  totalSections?: number;
  /** Current section index (0-based) */
  currentSectionIndex?: number;
  /** Whether to show section info (default: true if sections exist) */
  showSectionInfo?: boolean;
  /** Whether to show estimated time (default: true) */
  showEstimatedTime?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Sticky progress bar component for survey completion tracking.
 *
 * Features:
 * - Sticky positioning at top of viewport
 * - Real-time progress percentage
 * - Section navigation context
 * - Estimated time remaining
 * - Visual states for complete/incomplete
 *
 * @example
 * ```tsx
 * <SurveyProgressBar
 *   progress={progressData}
 *   currentSection="Demographics"
 *   totalSections={4}
 *   currentSectionIndex={1}
 * />
 * ```
 */
export function SurveyProgressBar({
  progress,
  currentSection,
  totalSections,
  currentSectionIndex,
  showSectionInfo = true,
  showEstimatedTime = true,
}: SurveyProgressBarProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  // Track scroll position for shadow effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const { answered, total, percentage, estimatedMinutesRemaining } = progress;
  const isComplete = percentage === 100;

  // Determine progress bar color
  const getProgressColor = () => {
    if (isComplete) return 'success';
    if (percentage >= 75) return 'primary';
    if (percentage >= 50) return 'primary';
    return 'primary';
  };

  // Format section display
  const sectionDisplay = currentSection && totalSections && currentSectionIndex !== undefined
    ? `Section ${currentSectionIndex + 1} of ${totalSections}`
    : currentSection || null;

  return (
    <Box
      className={`survey-progress-bar ${isScrolled ? 'survey-progress-bar--scrolled' : ''}`}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        py: 1.5,
        px: 2,
        transition: 'box-shadow 0.2s ease',
        boxShadow: isScrolled ? '0 2px 8px rgba(0, 0, 0, 0.08)' : 'none',
      }}
    >
      {/* Main content row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        {/* Left: Answered count */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isComplete ? (
            <TaskAltIcon sx={{ fontSize: 18, color: 'success.main' }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: isComplete ? 'success.main' : 'text.primary',
            }}
          >
            {answered} of {total} answered
          </Typography>
        </Box>

        {/* Right: Time remaining and percentage */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {showEstimatedTime && !isComplete && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatTimeRemaining(estimatedMinutesRemaining)}
              </Typography>
            </Box>
          )}

          <Chip
            label={`${percentage}%`}
            size="small"
            color={isComplete ? 'success' : 'default'}
            variant={isComplete ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 600,
              minWidth: 52,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      </Box>

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={getProgressColor()}
        sx={{
          height: 6,
          borderRadius: 1,
          bgcolor: alpha('#14B8A6', 0.1),
          '& .MuiLinearProgress-bar': {
            borderRadius: 1,
            transition: 'transform 0.3s ease-in-out',
          },
        }}
      />

      {/* Section info (optional) */}
      {showSectionInfo && sectionDisplay && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            {sectionDisplay}
            {currentSection && `: ${currentSection}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default SurveyProgressBar;
